import {
  Button,
  ErrorState,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableControls,
  TableHead,
  TableHeader,
  TableRow,
  TOUCH,
} from "@nymbl/nos2-rules";
import { useMemo, useState } from "react";
import { MonthSwitcher } from "@/components/team/MonthSwitcher";
import { OFF_KIND_FILL, OFF_KIND_OUTLINE } from "@/components/team/team-colors";
import { useTeamTimeOff } from "@/hooks/use-team";
import { monthParam } from "@/lib/team/months";
import { buildTimeline, OFF_KIND_LABELS } from "@/lib/team/time-off";
import type { IsoDate } from "@/lib/time/dates";
import { cn } from "@/lib/utils";

const TODAY_RING = "outline-2 outline-foreground outline-solid";

/** Team time off (TEAM-05 as a compact timeline; DECISIONS 2026-10-15). Any month can be opened. */
export function TimeOffTimeline({ initialMonth }: { initialMonth: IsoDate }) {
  const [month, setMonth] = useState(initialMonth);
  const [search, setSearch] = useState("");
  const query = useTeamTimeOff(monthParam(month));
  const timeline = useMemo(
    () =>
      query.data
        ? buildTimeline(query.data.members, query.data.rows, query.data.month, query.data.today)
        : null,
    [query.data],
  );
  const q = search.trim().toLowerCase();
  const shown = timeline
    ? timeline.rows.filter((r) => !q || r.personName.toLowerCase().includes(q))
    : [];

  return (
    <section aria-labelledby="time-off-h" className="space-y-3">
      {/* The week-grid exception (DECISIONS): a calendar shares the header line, no column menus. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 id="time-off-h" className="font-display text-lg font-bold">
            Time off
          </h2>
          <MonthSwitcher month={month} onChange={setMonth} scope="Time off" />
        </div>
        <TableControls value={search} onChange={setSearch} label="Search time off" />
      </div>
      {query.isError && (
        <ErrorState message="Time off didn't load" onRetry={() => void query.refetch()} />
      )}
      {query.isPending && <Skeleton className="h-40 w-full" />}
      {timeline && (
        <Table aria-label="Time off by day" scrollRegion>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-10 min-w-40 bg-background">Person</TableHead>
              {timeline.days.map((d) => (
                <TableHead
                  key={d.date}
                  className={cn(
                    "px-0.5 text-center text-xs",
                    d.today && "font-bold text-primary",
                    d.weekend && "text-muted-foreground",
                  )}
                >
                  <div>{d.dow}</div>
                  <div>{d.day}</div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={timeline.days.length + 1} className="py-8">
                  <div className="flex flex-col items-center gap-2 text-center">
                    <span className="font-semibold">No matches</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={TOUCH}
                      onClick={() => setSearch("")}
                    >
                      Clear filters
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {shown.map((row) => (
              <TableRow key={row.personId}>
                <TableCell className="sticky left-0 z-10 bg-background">
                  <div className="font-semibold">{row.personName}</div>
                  <div className="text-xs text-muted-foreground">{row.daysLabel}</div>
                </TableCell>
                {row.cells.map((c) => (
                  <TableCell key={c.date} className="px-0.5 py-1">
                    {c.kind ? (
                      <div
                        role="img"
                        aria-label={c.title}
                        title={c.title}
                        data-pending={c.pending ? "true" : undefined}
                        className={cn(
                          "h-6 min-w-5 rounded-sm",
                          c.pending ? OFF_KIND_OUTLINE[c.kind] : OFF_KIND_FILL[c.kind],
                          c.today && TODAY_RING,
                        )}
                      />
                    ) : (
                      <div
                        className={cn(
                          "h-6 min-w-5 rounded-sm",
                          c.weekend ? "bg-muted/40" : "bg-muted",
                          c.today && TODAY_RING,
                        )}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {timeline && timeline.kinds.length > 0 && (
        <ul className="flex flex-wrap gap-3 text-sm" aria-label="Legend">
          {timeline.kinds.map((k) => (
            <li key={k} className="flex items-center gap-1.5">
              <span className={cn("size-3 rounded-sm", OFF_KIND_FILL[k])} aria-hidden="true" />
              {OFF_KIND_LABELS[k]}
            </li>
          ))}
          {timeline.hasPending && (
            <li className="flex items-center gap-1.5">
              <span
                className="size-3 rounded-sm border-2 border-foreground bg-background"
                aria-hidden="true"
              />
              Pending
            </li>
          )}
        </ul>
      )}
    </section>
  );
}
